import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from '../../components/MobileLayout';
import { useVoices } from '../../../hooks/useApi';
import './MobileVoices.css';

const IconPlus   = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
const IconSearch = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconPlay   = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>;
const IconPause  = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>;
const IconTrash  = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>;

const WAVE_HEIGHTS = [5, 10, 7, 14, 9, 12, 6, 16, 8, 10, 5];
const GENDER_LABEL = { male: '男', female: '女' };

export default function MobileVoices() {
  const navigate = useNavigate();
  const [search, setSearch]       = useState('');
  const [page, setPage]           = useState(1);
  const [allVoices, setAllVoices] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const audioRef    = useRef(null);
  const sentinelRef = useRef(null);

  const { voices, loading, error, totalPages, refetch, deleteVoice } = useVoices('my', page, search);

  // Accumulate pages: reset on page 1, append on subsequent pages
  useEffect(() => {
    if (page === 1) {
      setAllVoices(voices);
    } else {
      setAllVoices((prev) => [...prev, ...voices]);
    }
  }, [voices]); // eslint-disable-line react-hooks/exhaustive-deps

  // IntersectionObserver: auto-load next page when sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loading && page < totalPages) {
          setPage((p) => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, page, totalPages]);

  const handleSelect = (voice) => {
    localStorage.setItem('selectedVoice', JSON.stringify(voice));
    navigate('/m/generate');
  };

  const handleDelete = async (voice) => {
    if (!window.confirm(`确认删除「${voice.name}」？`)) return;
    try {
      audioRef.current?.pause();
      if (playingId === voice.id) setPlayingId(null);
      await deleteVoice(voice.id);
    } catch (err) {
      alert(err.message || '删除失败');
    }
  };

  const handlePlayToggle = (e, voice) => {
    e.stopPropagation();
    if (!voice.sampleUrl) return;

    if (playingId === voice.id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }

    audioRef.current?.pause();
    const audio = new Audio(voice.sampleUrl);
    audioRef.current = audio;
    audio.play().catch(() => {});
    setPlayingId(voice.id);
    audio.onended = () => setPlayingId(null);
  };

  return (
    <MobileLayout title="我的声音">
      <div className="mvoi-root">
        {/* Search */}
        <div className="mvoi-search-wrap">
          <span className="mvoi-search-icon"><IconSearch /></span>
          <input
            className="mvoi-search"
            type="search"
            placeholder="搜索声音名称..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <div className="mvoi-list">
          {/* Create new */}
          <div
            className="mvoi-create-row"
            onClick={() => navigate('/m/create-voice', { state: { returnTo: '/m/voices' } })}
          >
            <div className="mvoi-create-circle"><IconPlus /></div>
            <div>
              <div className="mvoi-create-label">克隆新声音</div>
              <div className="mvoi-create-hint">录音或上传音频</div>
            </div>
          </div>

          {loading && page === 1 ? (
            <div className="mvoi-loading"><div className="mvoi-spinner" /></div>
          ) : error ? (
            <div className="mvoi-error">{error} <button onClick={() => refetch()}>重试</button></div>
          ) : allVoices.length === 0 && !loading ? (
            <div className="mvoi-empty">
              {search ? '没有匹配的声音' : '还没有克隆声音'}
            </div>
          ) : (
            allVoices.map((voice) => {
              const isPlaying = playingId === voice.id;
              const gender    = voice.gender || 'unknown';
              return (
                <div key={voice.id} className="mvoi-card">
                  <div className={`mvoi-wave-wrap${gender === 'male' ? ' male' : ''}`}>
                    <div className="mvoi-wave">
                      {WAVE_HEIGHTS.map((h, i) => (
                        <div key={i} className="mvoi-wave-bar" style={{ height: `${h}px` }} />
                      ))}
                    </div>
                    {GENDER_LABEL[gender] && (
                      <span className="mvoi-gender">{GENDER_LABEL[gender]}</span>
                    )}
                  </div>

                  <div className="mvoi-info">
                    <div className="mvoi-name">{voice.name}</div>
                    <div className="mvoi-sub">{voice.language || '中文'}</div>
                  </div>

                  <div className="mvoi-actions">
                    {voice.sampleUrl && (
                      <button
                        className={`mvoi-btn play${isPlaying ? ' playing' : ''}`}
                        onClick={(e) => handlePlayToggle(e, voice)}
                        aria-label={isPlaying ? '暂停' : '试听'}
                      >
                        {isPlaying ? <IconPause /> : <IconPlay />}
                      </button>
                    )}
                    <button className="mvoi-btn select" onClick={() => handleSelect(voice)}>
                      选择
                    </button>
                    <button className="mvoi-btn del" onClick={() => handleDelete(voice)}>
                      <IconTrash />
                    </button>
                  </div>
                </div>
              );
            })
          )}

          {/* Sentinel for IntersectionObserver */}
          <div ref={sentinelRef} className="mvoi-sentinel" />

          {/* Bottom loading indicator */}
          {loading && page > 1 && (
            <div className="mvoi-bottom-loading"><div className="mvoi-spinner" /></div>
          )}

          {/* All loaded hint */}
          {!loading && totalPages > 0 && page >= totalPages && allVoices.length > 0 && (
            <div className="mvoi-all-loaded">已加载全部声音</div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
}
